import { IconNames } from '@blueprintjs/icons';
import { Dialog } from '@strata/react-dialog';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { type WorklistTask } from '@strata/react-worklist';
import { useState } from 'react';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { useAppDispatch } from 'store';
import { usePostDenyLddAccessMutation } from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

interface DenyLddAccessDialogProps {
    isOpen: boolean;
    worklistTask?: WorklistTask;
    onClose: () => void;
}

export const DenyLddAccessDialog = ({
    isOpen,
    worklistTask = undefined,
    onClose,
}: DenyLddAccessDialogProps) => {
    const dispatch = useAppDispatch();
    const writeWorklist = usePermissionCheck(Permissions.WRITE_WORKLIST);
    const [postDenyLddAccess] = usePostDenyLddAccessMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    return (
        <Dialog
            isOpen={isOpen}
            dialogTitle="No Access"
            headerIcon={IconNames.WARNING_SIGN}
            primaryButtonLabel="No Access"
            isPrimaryButtonDisabled={!writeWorklist || isSubmitting}
            secondaryButtonLabel="Cancel"
            onSecondaryButtonClick={onClose}
            onPrimaryButtonClick={() => {
                void (async () => {
                    try {
                        if (worklistTask && !isSubmitting) {
                            setIsSubmitting(true);
                            await postDenyLddAccess({ taskId: worklistTask.task_id }).unwrap();
                            await dispatch(fetchWorklistTasks());
                            SuccessToast({ message: 'LDD access denied successfully.' });
                            onClose();
                        }
                    } catch (err) {
                        handleError('An error occurred denying LDD access.', err as Error);
                    } finally {
                        setIsSubmitting(false);
                    }
                })();
            }}
            dialogContent={
                <>
                    <div className="margin__bottom--large">
                        Confirm your pharmacy does not have access to:
                    </div>
                    <DetailLabelValuePair
                        label="Product"
                        value={worklistTask?.task_info?.product_name}
                    />
                </>
            }
        />
    );
};
